import React, { useState } from 'react';
import { useAuth } from '../../AuthContext';
import {
  Drawer,
  Button,
  Input,
  Form,
  Space,
  Tag,
  message,
  Tooltip,
  Switch,
} from 'antd';
import {
  SaveOutlined,
  ThunderboltOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';

const { TextArea } = Input;

export default function SaveProductDrawer({
  open,
  onClose,
  onSaved,
  filters,
  targetCount,
  activitySummary,
  updateProduct = null, // si défini : mode mise à jour du produit existant
}) {
  const { user } = useAuth();
  const isUpdate = !!updateProduct;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [createPlaylist, setCreatePlaylist] = useState(false);

  // Pré-remplir le formulaire en mode édition
  React.useEffect(() => {
    if (open && isUpdate && updateProduct) {
      form.setFieldsValue({
        slug: updateProduct.slug,
        name: updateProduct.name,
        category: updateProduct.category || '',
        description: updateProduct.description || '',
        long_description: updateProduct.long_description || '',
        features: Array.isArray(updateProduct.features)
          ? updateProduct.features.join('\n')
          : updateProduct.features || '',
      });
    }
    if (!open) form.resetFields();
  }, [open, isUpdate, updateProduct, form]);

  const handleGenerateAI = async () => {
    setAiLoading(true);
    try {
      const token = localStorage.getItem('prae.jwt');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/packaged-products/generate-ai`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ filters, targetCount, activitySummary }),
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      const generated = await res.json();
      form.setFieldsValue({
        name: generated.name,
        slug: generated.slug,
        category: generated.category,
        description: generated.description,
        long_description: generated.long_description,
        features: Array.isArray(generated.features)
          ? generated.features.join('\n')
          : generated.features,
      });
      message.success("Champs générés par l'IA !");
    } catch (err) {
      message.error(`Erreur IA : ${err.message}`);
    } finally {
      setAiLoading(false);
    }
  };

  const handleSave = async values => {
    setLoading(true);

    try {
      const token = localStorage.getItem('prae.jwt');

      // Build geography labels from departments and communes
      const geographyLabels = [];
      if (filters.depts && filters.depts.length > 0) {
        geographyLabels.push(...filters.depts.map(d => `Département ${d}`));
      }
      if (filters.communes && filters.communes.length > 0) {
        geographyLabels.push(`${filters.communes.length} communes`);
      }

      // Build sector labels from NAF codes
      const sectorLabels =
        activitySummary.length > 0
          ? activitySummary.map(item => item.label || item.key)
          : filters.naf || [];

      // Parse features from textarea (one per line)
      const features = values.features
        ? values.features.split('\n').filter(f => f.trim())
        : [];

      const body = {
        slug: values.slug,
        name: values.name,
        description: values.description,
        long_description: values.long_description,
        category: values.category,
        filter_departments: filters.depts || [],
        filter_communes: filters.communes || [],
        filter_communes_insee: filters.communesInsee || [],
        filter_naf: filters.naf || [],
        filter_email_only: filters.emailOnly || false,
        geography_labels: geographyLabels,
        sector_labels: sectorLabels,
        features,
        establishment_count: targetCount || 0,
      };

      const url = isUpdate
        ? `${process.env.REACT_APP_API_URL}/packaged-products/${updateProduct.slug}`
        : `${process.env.REACT_APP_API_URL}/packaged-products`;

      const res = await fetch(url, {
        method: isUpdate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      const json = await res.json();

      if (!isUpdate && createPlaylist) {
        const playlistRes = await fetch(
          `${process.env.REACT_APP_API_URL}/playlists/from-selection`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              name: values.name,
              userId: user?.id,
              depts: filters.depts || [],
              communes: filters.communes || [],
              communesInsee: filters.communesInsee || [],
              naf: filters.naf || [],
              emailOnly: filters.emailOnly || false,
              expectedCount: targetCount,
            }),
          }
        );
        if (!playlistRes.ok) {
          const err = await playlistRes.json().catch(() => ({}));
          message.warning(
            `Produit créé mais erreur playlist : ${err.error || playlistRes.status}`
          );
        } else {
          const playlist = await playlistRes.json();
          message.success(
            `Produit créé + playlist créée (${playlist.tracks} contacts)`
          );
        }
      } else if (isUpdate) {
        message.success(
          `Produit mis à jour ! ${targetCount?.toLocaleString()} établissements`
        );
        onSaved?.(json.slug);
        onClose();
        return;
      } else {
        message.success(
          `Produit packagé créé ! Slug: ${json.slug}, ${targetCount} établissements`
        );
      }

      form.resetFields();
      onClose();
    } catch (err) {
      message.error(`Erreur lors de la sauvegarde : ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title={
        isUpdate
          ? `Mettre à jour : ${updateProduct.name}`
          : 'Sauvegarder comme produit packagé'
      }
      placement="right"
      onClose={onClose}
      open={open}
      width={600}
      zIndex={1200}
      footer={
        <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Tooltip title="L'IA analyse les filtres sélectionnés et pré-remplit tous les champs automatiquement">
            <Button
              icon={<ThunderboltOutlined />}
              onClick={handleGenerateAI}
              loading={aiLoading}
              style={{ borderColor: '#7c3aed', color: '#7c3aed' }}
            >
              Générer avec l'IA
            </Button>
          </Tooltip>
          <Space>
            <Button onClick={onClose}>Annuler</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={() => form.submit()}
              loading={loading}
            >
              {isUpdate ? 'Mettre à jour' : 'Sauvegarder le produit'}
            </Button>
          </Space>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSave}>
        {/* Summary Section */}
        <div
          style={{
            background: '#f5f5f5',
            padding: 16,
            borderRadius: 8,
            marginBottom: 24,
          }}
        >
          <h4 style={{ marginTop: 0, marginBottom: 12 }}>
            Critères de sélection
          </h4>

          {/* Departments */}
          {filters.depts && filters.depts.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <strong>Départements :</strong>
              <div style={{ marginTop: 4 }}>
                {filters.depts.map(d => (
                  <Tag key={d} color="blue" style={{ marginBottom: 4 }}>
                    {d}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          {/* Communes */}
          {filters.communes && filters.communes.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <strong>Communes :</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="cyan">{filters.communes.length} communes</Tag>
              </div>
            </div>
          )}

          {/* Activities */}
          {activitySummary && activitySummary.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <strong>Activités :</strong>
              <div style={{ marginTop: 4 }}>
                {activitySummary.slice(0, 5).map((act, i) => (
                  <Tag key={i} color="purple" style={{ marginBottom: 4 }}>
                    {act.label || act.key}
                  </Tag>
                ))}
                {activitySummary.length > 5 && (
                  <Tag color="default">
                    +{activitySummary.length - 5} autres
                  </Tag>
                )}
              </div>
            </div>
          )}

          {/* Email Filter */}
          {filters.emailOnly && (
            <div>
              <Tag color="green">Avec e-mail uniquement</Tag>
            </div>
          )}

          {/* Target Count */}
          <div style={{ marginTop: 12, fontSize: 16, fontWeight: 'bold' }}>
            {targetCount?.toLocaleString() || 0} établissements
          </div>
        </div>

        {/* Form Fields */}
        <Form.Item
          label="Slug (URL)"
          name="slug"
          rules={[
            { required: true, message: 'Le slug est requis' },
            {
              pattern: /^[a-z0-9-]+$/,
              message: 'Uniquement lettres minuscules, chiffres et tirets',
            },
          ]}
          extra="Ex: agences-immobilieres-idf (utilisé dans l'URL)"
        >
          <Input placeholder="agences-immobilieres-idf" disabled={isUpdate} />
        </Form.Item>

        <Form.Item
          label="Nom du produit"
          name="name"
          rules={[{ required: true, message: 'Le nom est requis' }]}
        >
          <Input placeholder="Agences immobilières Île-de-France" />
        </Form.Item>

        <Form.Item
          label="Catégorie"
          name="category"
          extra="Ex: Immobilier, Restauration, Automobile, etc."
        >
          <Input placeholder="Immobilier" />
        </Form.Item>

        <Form.Item
          label="Description courte"
          name="description"
          rules={[{ required: true, message: 'La description est requise' }]}
        >
          <TextArea
            rows={2}
            placeholder="Base de données complète des agences immobilières en Île-de-France"
          />
        </Form.Item>

        <Form.Item
          label="Description détaillée"
          name="long_description"
          extra="Description longue pour la page produit"
        >
          <TextArea
            rows={4}
            placeholder="Cette base de données contient l'ensemble des agences immobilières actives en Île-de-France..."
          />
        </Form.Item>

        <Form.Item
          label="Caractéristiques"
          name="features"
          extra="Une caractéristique par ligne (ex: 'Emails professionnels vérifiés')"
        >
          <TextArea
            rows={6}
            placeholder={
              "Emails professionnels vérifiés\nAdresses complètes et géolocalisées\nMise à jour mensuelle\nFormat CSV prêt à l'emploi\nConforme RGPD\nLivraison immédiate"
            }
          />
        </Form.Item>

        {!isUpdate && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '12px 16px',
              background: '#f0f4ff',
              borderRadius: 8,
              marginTop: 8,
            }}
          >
            <UnorderedListOutlined style={{ color: '#4f46e5' }} />
            <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>
              Créer aussi une playlist avec ces filtres
            </span>
            <Switch checked={createPlaylist} onChange={setCreatePlaylist} />
          </div>
        )}
      </Form>
    </Drawer>
  );
}
